"use client";

import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";

interface SpeakingIndicatorProps {
  isSpeaking: boolean;
  size?: "small" | "normal";
  position?: "absolute" | "relative";
  variant?: "dots" | "icon" | "wave";
}

export default function SpeakingIndicator({
  isSpeaking,
  size = "normal",
  position = "absolute",
  variant = "icon",
}: SpeakingIndicatorProps) {
  if (variant === "icon") {
    return (
      <motion.div
        animate={
          isSpeaking
            ? {
                scale: [1, 1.1, 1],
                opacity: [0.8, 1, 0.8],
              }
            : {
                scale: 1,
                opacity: 0.6,
              }
        }
        className={cn(
          "flex items-center justify-center",
          position === "absolute" ? "absolute -bottom-1 left-1/2 -translate-x-1/2" : "",
          size === "small" ? "h-4 w-4" : "h-5 w-5",
        )}
        transition={
          isSpeaking
            ? {
                duration: 0.8,
                repeat: Infinity,
                ease: "easeInOut",
              }
            : {
                duration: 0.2,
              }
        }
      >
        <Icon
          className={cn(
            "transition-colors",
            isSpeaking ? "text-accent-foreground" : "text-muted-foreground",
            size === "small" ? "h-3 w-3" : "h-4 w-4",
          )}
          icon="material-symbols:mic"
        />
      </motion.div>
    );
  }

  if (variant === "wave") {
    const barCount = size === "small" ? 3 : 4;
    const barWidth = size === "small" ? "w-0.5" : "w-1";
    const barHeight = size === "small" ? ["h-1", "h-1.5", "h-2"] : ["h-2", "h-3", "h-4", "h-3"];

    return (
      <div
        className={cn(
          "flex items-end gap-0.5",
          position === "absolute" ? "absolute -bottom-1 left-1/2 -translate-x-1/2" : "",
        )}
      >
        {Array.from({ length: barCount }).map((_, index) => (
          <motion.div
            key={index}
            animate={
              isSpeaking
                ? {
                    scaleY: [0.3, 1, 0.3],
                    opacity: [0.5, 1, 0.5],
                  }
                : {
                    scaleY: 0.3,
                    opacity: 0.3,
                  }
            }
            className={cn(
              barWidth,
              barHeight[index % barHeight.length],
              "bg-accent-foreground origin-bottom rounded-full",
            )}
            transition={
              isSpeaking
                ? {
                    duration: 0.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: index * 0.1,
                  }
                : {
                    duration: 0.2,
                  }
            }
          />
        ))}
      </div>
    );
  }

  // Original dots variant
  const dotSize = size === "small" ? "h-0.5 w-0.5" : "h-1 w-1";
  const gapSize = size === "small" ? "gap-0.5" : "gap-0.5";

  return (
    <div
      className={cn(
        "flex",
        gapSize,
        position === "absolute" ? "absolute -bottom-1 left-1/2 -translate-x-1/2" : "",
      )}
    >
      {Array.from({ length: 3 }).map((_, index) => (
        <motion.div
          key={index}
          animate={
            isSpeaking
              ? {
                  scale: [1, 1.3, 1],
                  opacity: [0.5, 1, 0.5],
                }
              : {
                  scale: 1,
                  opacity: 0.3,
                }
          }
          className={cn(dotSize, "bg-accent-foreground rounded-full")}
          transition={
            isSpeaking
              ? {
                  duration: 0.6,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: index * 0.2,
                }
              : {
                  duration: 0.2,
                }
          }
        />
      ))}
    </div>
  );
}

// Add cn utility function if not already available
function cn(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(" ");
}
